import { useMutation, useQueryClient } from "@tanstack/react-query";

import { buildRentalUpdateRequestBody } from "features/subscriptions/helpers/rentalSubscriptions";
import type { getCouponUpdateState } from "features/subscriptions/utils";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import useWorkspaceCacheUpdate from "common/datahooks/useWorkspaceCacheUpdate";
import { patch } from "common/helpers/HTTP";
import type { DetailedWorkspace, RentedSubscription } from "common/types";

type CouponUpdateState = ReturnType<typeof getCouponUpdateState>;

async function addRentalAccountsRequest(
  workspaceId: string,
  rentalCount: number,
  couponUpdateState: CouponUpdateState,
  planPriceId?: string,
): Promise<{ workspace: DetailedWorkspace }> {
  const { workspace } = await patch(`workspaces/${workspaceId}/rental`, {
    rental_count: rentalCount,
    item_price_id: planPriceId,
    ...(couponUpdateState && {
      coupon: couponUpdateState.coupon,
      remove_coupon: couponUpdateState.removeCoupon,
    }),
  });
  return { workspace };
}

// Updates an existing rental order (seats and/or billing period)
async function updateRentalSubscriptionRequest(
  workspaceId: string,
  subscriptionId: string,
  body: Record<string, unknown>,
): Promise<{ subscription: RentedSubscription }> {
  const { subscription } = await patch(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/rental`,
    body,
  );
  return { subscription };
}

export default function useRentalMutations() {
  const queryClient = useQueryClient();

  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { updateCachedWorkspaceDetails, updateCachedWorkspaceList } =
    useWorkspaceCacheUpdate();

  const {
    mutateAsync: addRentalAccounts,
    isPending: isAddingRentalAccounts,
    error: addRentalAccountsError,
  } = useMutation({
    mutationFn: ({
      rentalCount,
      couponUpdateState,
      planPriceId,
    }: {
      rentalCount: number;
      couponUpdateState: CouponUpdateState;
      planPriceId?: string;
    }) =>
      addRentalAccountsRequest(
        workspaceId,
        rentalCount,
        couponUpdateState,
        planPriceId,
      ),
    onSuccess: ({ workspace }) => {
      updateCachedWorkspaceDetails(workspaceId, () => workspace);
      updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
        draftWorkspace.rental_account_count = workspace.rental_account_count;
        draftWorkspace.has_active_rental_subscription =
          workspace.has_active_rental_subscription;
      });

      workspace.rental_subscriptions?.forEach(({ id }) => {
        queryClient.invalidateQueries({
          queryKey:
            getQueryKeys(workspaceId).subscriptionKeys.renewalEstimate(id),
        });
      });
    },
  });

  const {
    mutateAsync: updateRentalSubscription,
    isPending: isUpdatingRentalSubscription,
    error: updateRentalSubscriptionError,
  } = useMutation({
    mutationFn: ({
      subscriptionId,
      count,
      planPricingId,
      accountIds,
      couponUpdateState,
    }: {
      subscriptionId: string;
      count: number;
      planPricingId: string;
      accountIds: string[];
      couponUpdateState: CouponUpdateState;
    }) => {
      const body = buildRentalUpdateRequestBody({
        count,
        planPricingId,
        accountIds,
        couponUpdateState,
      });
      return updateRentalSubscriptionRequest(workspaceId, subscriptionId, body);
    },
    onSuccess: ({ subscription }) => {
      updateCachedWorkspaceDetails(workspaceId, (draftWorkspace) => {
        const index = draftWorkspace.rental_subscriptions.findIndex(
          ({ id }) => id === subscription.id,
        );
        if (index !== -1) {
          draftWorkspace.rental_subscriptions[index] = subscription;
        }
      });

      queryClient.invalidateQueries({
        queryKey: getQueryKeys(workspaceId).subscriptionKeys.renewalEstimate(
          subscription.id,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: CUSTOMER_QUERY_KEYS.workspaces.details(workspaceId),
      });
    },
  });

  return {
    addRentalAccounts,
    isAddingRentalAccounts,
    addRentalAccountsError,

    updateRentalSubscription,
    isUpdatingRentalSubscription,
    updateRentalSubscriptionError,
  };
}
